import type { Editor, JSONContent } from '@tiptap/core'
import type { ExportDocxOptions } from '@tiptap-pro/extension-export-docx'

// Replaces characters that are invalid in filenames on common platforms with
// spaces, so the words they separated stay apart.
const toDocxFileName = (title?: string): string => {
  const base = (title ?? '')
    .replace(/[\\/:*?"<>|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  return `${base || 'document'}.docx`
}

type Thread = NonNullable<ExportDocxOptions['comments']>['threads'][number]

// The same threads the ExportDocx command would read from the comments
// provider. Comment bodies go through the converter too, so their images get
// the same fallback.
const openCommentThreads = (
  editor: Editor,
  withDocxImages: (node: JSONContent) => JSONContent
): Thread[] => {
  const provider = editor.extensionManager.extensions.find(
    ({ name }) => name === 'comments'
  )?.options?.provider
  const threads = provider?.getThreads?.({ types: ['unarchived'] })
  if (!Array.isArray(threads)) return []

  return threads.map((thread: Thread) => ({
    ...thread,
    comments: thread.comments.map((comment) => ({
      ...comment,
      content:
        typeof comment.content === 'string'
          ? comment.content
          : withDocxImages(comment.content),
    })),
  }))
}

/**
 * Exports the editor's current content as a Word document and starts a
 * browser download. The download is named after `title` when given, falling
 * back to `document.docx`.
 *
 * This calls the converter directly instead of the ExportDocx command: the
 * command converts plain `image` nodes itself, with no way to catch a failed
 * fetch, and has no error callback, so one blocked image stopped the export.
 *
 * The converter loads on first use, so it stays out of the editor bundle.
 */
export const exportEditorDocx = async (editor: Editor, title?: string) => {
  const [
    { exportDocx },
    { docxExportCustomNodes, docxTableOverrides, withDocxImages },
  ] = await Promise.all([
    import('@tiptap-pro/extension-export-docx'),
    import('./docxCustomNodes'),
  ])

  const result = await exportDocx({
    document: withDocxImages(editor.getJSON()),
    exportType: 'blob',
    customNodes: docxExportCustomNodes,
    styleOverrides: {},
    tableOverrides: docxTableOverrides,
    comments: { threads: openCommentThreads(editor, withDocxImages) },
  })

  const url = URL.createObjectURL(result as Blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = toDocxFileName(title)
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}
